import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Star, Award, User, UserPlus, IndianRupee, Clock, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { TutorInfoSkeleton } from '../../components/ui/skeletons';
import { studentService } from '../../services/studentService';
import { teacherQueryService } from '../../services/teacherQueryService';
import { useToast } from '../../hooks/use-toast';

const timeSlots = [
  { value: 'morning', label: 'Morning (6 AM - 12 PM)' },
  { value: 'afternoon', label: 'Afternoon (12 PM - 5 PM)' },
  { value: 'evening', label: 'Evening (5 PM - 9 PM)' },
  { value: 'flexible', label: 'Flexible' },
];

const hoursOptions = [
  { value: '1', label: '1 hour' },
  { value: '2', label: '2 hours' },
  { value: '3', label: '3 hours' },
  { value: '4', label: '4 hours' },
  { value: '5', label: '5 hours' },
  { value: '6', label: '6 hours' },
];

const TeacherQueryForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [budget, setBudget] = useState('');
  const [hoursPerDay, setHoursPerDay] = useState('');
  const [preferredGender, setPreferredGender] = useState('any');
  const [preferredTime, setPreferredTime] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    setLoadingStudents(true);
    try {
      const response = await studentService.getStudents();
      setStudents(response.data?.students || response.data || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load students',
        variant: 'destructive',
      });
    } finally {
      setLoadingStudents(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedStudentId) {
      newErrors.student = 'Please select a student';
    }
    if (!budget || parseFloat(budget) < 100) {
      newErrors.budget = 'Please enter a valid budget (minimum ₹100)';
    }
    if (!hoursPerDay) {
      newErrors.hoursPerDay = 'Please select hours per day';
    }
    if (!preferredTime) {
      newErrors.preferredTime = 'Please select a preferred time';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const data = {
        student_id: parseInt(selectedStudentId, 10),
        budget: parseFloat(budget),
        hours_per_day: parseInt(hoursPerDay, 10),
        preferred_gender: preferredGender,
        preferred_time: preferredTime,
        notes: notes.trim() || undefined,
      };

      await teacherQueryService.submitQuery(data);

      toast({
        title: 'Success',
        description: 'Your teacher query has been submitted successfully!',
      });

      navigate('/customer/dashboard');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to submit query',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>

        <h1 className="text-2xl font-bold mb-6">Submit Teacher Query</h1>

        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Find Your Perfect Tutor
              </h2>
              <p className="text-gray-600">
                Tell us your requirements and we'll help you find the best tutor for your needs.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Query Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label>
                  Select Student <span className="text-red-500">*</span>
                </Label>
                {loadingStudents ? (
                  <div className="flex items-center gap-2 py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm text-gray-500">Loading students...</span>
                  </div>
                ) : students.length === 0 ? (
                  <div className="border rounded-lg p-4 text-center">
                    <User className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600 mb-3">No students added yet</p>
                    <Link to="/customer/students/add">
                      <Button type="button" variant="outline" size="sm" className="gap-1">
                        <UserPlus className="h-4 w-4" />
                        Add Student
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <Select
                    value={selectedStudentId}
                    onValueChange={(value) => {
                      setSelectedStudentId(value);
                      if (errors.student) {
                        setErrors((prev) => ({ ...prev, student: '' }));
                      }
                    }}
                  >
                    <SelectTrigger className={errors.student ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select a student" />
                    </SelectTrigger>
                    <SelectContent>
                      {students.map((student) => (
                        <SelectItem key={student.id} value={String(student.id)}>
                          {student.name}
                          {student.class_name && ` (Class ${student.class_name})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {errors.student && (
                  <p className="text-sm text-red-500" role="alert">{errors.student}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="budget">
                  Budget per Month (₹) <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input
                    id="budget"
                    type="number"
                    placeholder="Enter your budget"
                    value={budget}
                    onChange={(e) => {
                      setBudget(e.target.value);
                      if (errors.budget) {
                        setErrors((prev) => ({ ...prev, budget: '' }));
                      }
                    }}
                    className={`pl-10 ${errors.budget ? 'border-red-500' : ''}`}
                    min="100"
                    max="100000"
                  />
                </div>
                {errors.budget && (
                  <p className="text-sm text-red-500" role="alert">{errors.budget}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>
                  Hours per Day <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={hoursPerDay}
                  onValueChange={(value) => {
                    setHoursPerDay(value);
                    if (errors.hoursPerDay) {
                      setErrors((prev) => ({ ...prev, hoursPerDay: '' }));
                    }
                  }}
                >
                  <SelectTrigger className={errors.hoursPerDay ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select hours per day" />
                  </SelectTrigger>
                  <SelectContent>
                    {hoursOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.hoursPerDay && (
                  <p className="text-sm text-red-500" role="alert">{errors.hoursPerDay}</p>
                )}
              </div>

              <div className="space-y-3">
                <Label>
                  Preferred Gender <span className="text-red-500">*</span>
                </Label>
                <RadioGroup
                  value={preferredGender}
                  onValueChange={setPreferredGender}
                  className="flex flex-wrap gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="male" id="male" />
                    <Label htmlFor="male" className="font-normal cursor-pointer">Male</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="female" id="female" />
                    <Label htmlFor="female" className="font-normal cursor-pointer">Female</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="any" id="any" />
                    <Label htmlFor="any" className="font-normal cursor-pointer">Any</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label>
                  Preferred Time <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={preferredTime}
                  onValueChange={(value) => {
                    setPreferredTime(value);
                    if (errors.preferredTime) {
                      setErrors((prev) => ({ ...prev, preferredTime: '' }));
                    }
                  }}
                >
                  <SelectTrigger className={errors.preferredTime ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select preferred time">
                      {preferredTime && (
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          {timeSlots.find(t => t.value === preferredTime)?.label}
                        </div>
                      )}
                      {!preferredTime && 'Select preferred time'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((slot) => (
                      <SelectItem key={slot.value} value={slot.value}>
                        {slot.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.preferredTime && (
                  <p className="text-sm text-red-500" role="alert">{errors.preferredTime}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  placeholder="Any specific requirements or preferences for the tutor?"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  maxLength={500}
                />
                <p className="text-xs text-gray-500">{notes.length}/500 characters</p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  disabled={submitting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || loadingStudents || students.length === 0}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Query'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TeacherQueryForm;
